import { useState } from 'react'
import { formatScore } from './utils.js'

function mapBounds(points, ltmPoints, current) {
  const xs = points.map((p) => p.x)
  const ys = points.map((p) => p.y)
  for (const p of ltmPoints || []) {
    xs.push(p.x)
    ys.push(p.y)
  }
  if (current) {
    xs.push(current.x)
    ys.push(current.y)
  }
  if (!xs.length) return { minX: -1, maxX: 1, minY: -1, maxY: 1 }
  let minX = Math.min(...xs)
  let maxX = Math.max(...xs)
  let minY = Math.min(...ys)
  let maxY = Math.max(...ys)
  const padX = Math.max((maxX - minX) * 0.08, 0.05)
  const padY = Math.max((maxY - minY) * 0.08, 0.05)
  if (maxX - minX < 1e-9) {
    minX -= 1
    maxX += 1
  } else {
    minX -= padX
    maxX += padX
  }
  if (maxY - minY < 1e-9) {
    minY -= 1
    maxY += 1
  } else {
    minY -= padY
    maxY += padY
  }
  return { minX, maxX, minY, maxY }
}

export default function DreamMapPanel({ map, selected, onSelect, onOpenDetail }) {
  const [hovered, setHovered] = useState(null)

  if (!map || map.enabled === false) {
    return (
      <div className="mobi-map">
        <span className="dm-section">DREAM MAP</span>
        <div className="dm-dim" style={{ marginTop: 8 }}>
          unavailable
        </div>
      </div>
    )
  }

  const points = map.points || []
  const ltmPoints = map.ltm_points || []
  const current = map.current
  const ev = map.explained_variance_ratio || {}
  const pc1pct = ((ev.pc1 ?? 0) * 100).toFixed(0)
  const pc2pct = ((ev.pc2 ?? 0) * 100).toFixed(0)
  const sampleN = map.sample_size ?? map.valid_feature_count ?? points.length
  const W = 360
  const H = 220
  const pad = { l: 28, r: 12, t: 12, b: 28 }
  const { minX, maxX, minY, maxY } = mapBounds(points, ltmPoints, current)
  const toSvg = (x, y) => {
    const sx = pad.l + ((x - minX) / (maxX - minX)) * (W - pad.l - pad.r)
    const sy = pad.t + (1 - (y - minY) / (maxY - minY)) * (H - pad.t - pad.b)
    return [sx, sy]
  }
  const tip = hovered || selected

  return (
    <div className="mobi-map">
      <div className="mobi-panel-head">
        <span className="dm-section">DREAM MAP</span>
        <button
          type="button"
          className="mobi-link"
          onClick={() => onOpenDetail?.({ kind: 'map', map, point: tip })}
        >
          detail
        </button>
      </div>
      <div className="mobi-map-meta dm-dim">
        {map.projection_version || 'pca-v2-explore'} · explored {sampleN}
        {ltmPoints.length ? ` · ltm ${ltmPoints.length}` : ''} · PC1 {pc1pct}% · PC2{' '}
        {pc2pct}%
      </div>
      <svg
        className="dm-map-svg mobi-map-svg"
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label="Dream map exploration PCA"
      >
        <line
          x1={pad.l}
          y1={H - pad.b}
          x2={W - pad.r}
          y2={H - pad.b}
          className="dm-map-axis"
        />
        <line
          x1={pad.l}
          y1={pad.t}
          x2={pad.l}
          y2={H - pad.b}
          className="dm-map-axis"
        />
        <text x={W - pad.r} y={H - 8} className="dm-map-label" textAnchor="end">
          PC1 →
        </text>
        <text
          x={10}
          y={pad.t + 8}
          className="dm-map-label"
          transform={`rotate(-90 10 ${pad.t + 36})`}
        >
          ↑ PC2
        </text>
        {points.map((p) => {
          const [sx, sy] = toSvg(p.x, p.y)
          const isSel =
            selected &&
            (selected.experience_id === p.experience_id ||
              selected.generation === p.generation)
          return (
            <circle
              key={`e-${p.experience_id ?? p.generation}`}
              cx={sx}
              cy={sy}
              r={isSel ? 3 : 1.75}
              className={`dm-map-pt${isSel ? ' dm-map-pt-sel' : ''}`}
              onMouseEnter={() => setHovered(p)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSelect?.(p)}
            />
          )
        })}
        {ltmPoints.map((p) => {
          const [sx, sy] = toSvg(p.x, p.y)
          return (
            <circle
              key={`m-${p.memory_id ?? p.generation}`}
              cx={sx}
              cy={sy}
              r={4}
              className="dm-map-ltm"
              onMouseEnter={() => setHovered(p)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSelect?.(p)}
            />
          )
        })}
        {current &&
          (() => {
            const [sx, sy] = toSvg(current.x, current.y)
            return <circle cx={sx} cy={sy} r={5} className="dm-map-current" />
          })()}
      </svg>
      {tip && (
        <div className="mobi-map-tip dm-dim">
          {tip.kind === 'ltm' ? 'ltm ' : ''}#{tip.generation ?? tip.memory_id ?? '·'} ·{' '}
          {tip.generator || '—'} · I {formatScore(tip.interest)}
          {tip.memory_value != null ? ` · V ${formatScore(tip.memory_value)}` : ''}
        </div>
      )}
      {!points.length && (
        <div className="dm-dim" style={{ marginTop: 6, fontSize: 12 }}>
          sparse — waiting for exploration sample
        </div>
      )}
    </div>
  )
}
